"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

const delayClassMap = {
  0: "delay-0",
  10: "delay-100",
  15: "delay-150",
  20: "delay-200",
  30: "delay-300",
} as const;

function getDelayClass(delay: number) {
  return delayClassMap[Math.round(delay * 100) as keyof typeof delayClassMap] ?? "delay-0";
}

function useVisible(threshold = 0.1) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  return [ref, visible] as const;
}

function Reveal({
  children,
  delay = 0,
  from = "bottom",
}: {
  children: ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, visible] = useVisible();

  const fromClasses = {
    bottom: "translate-y-8",
    left: "-translate-x-8",
    right: "translate-x-8",
  } as const;

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0",
        fromClasses[from],
        "transition-all duration-[650ms] ease-out",
        getDelayClass(delay),
        visible && "opacity-100 translate-x-0 translate-y-0"
      )}
    >
      {children}
    </div>
  );
}

const SERVICES = [
  {
    n: "01",
    title: "Native iOS & Android Apps",
    body:
      "High-performance applications developed specifically for each platform using Swift (iOS) and Kotlin (Android). Maximum performance, platform-specific features and the best possible user experience.",
    img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80",
    tags: ["Swift / SwiftUI", "Kotlin / Jetpack", "Native performance"],
  },
  {
    n: "02",
    title: "Cross-Platform Development",
    body:
      "Cost-effective solutions using React Native and Flutter — one codebase that runs seamlessly on both iOS and Android. Faster time-to-market, lower cost and consistent user experience.",
    img: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&q=80",
    tags: ["React Native", "Flutter", "Expo"],
  },
  {
    n: "03",
    title: "Enterprise Mobile Solutions",
    body:
      "Secure, scalable applications for large organisations — field force apps, employee portals, B2B platforms, with enterprise system integration, advanced security and multi-role access.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
    tags: ["B2B platforms", "Multi-role access", "Enterprise integration"],
  },
  {
    n: "04",
    title: "UI/UX Design",
    body:
      "User-centric design driven by research and usability testing. Wireframes, prototypes, interaction design and pixel-perfect interfaces that users love from the first tap.",
    img: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&q=80",
    tags: ["Wireframing", "Prototyping", "Usability testing"],
  },
  {
    n: "05",
    title: "Backend & API Development",
    body:
      "Robust, scalable server-side infrastructure and RESTful API integrations that power reliable, fast and secure mobile applications at any scale.",
    img: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&q=80",
    tags: ["REST / GraphQL", "Firebase", "Node.js / Python"],
  },
  {
    n: "06",
    title: "App Maintenance & Updates",
    body:
      "Continuous support, OS update compatibility, bug fixes, performance optimisation, security patches and regular feature releases to keep your app current and competitive.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80",
    tags: ["OS compatibility", "Security patches", "Feature releases"],
  },
];

const TECH_STACK = [
  { platform: "Native iOS", tech: "Swift, SwiftUI, Xcode, Core Data, ARKit" },
  { platform: "Native Android", tech: "Kotlin, Jetpack Compose android Studio" },
  { platform: "Cross-Platform", tech: "React Native, Flutter, Expo" },
  { platform: "Backend", tech: "Node.js, Python, Firebase, MongoDB, GraphQL" },
  { platform: "Payments", tech: "Razorpay, Stripe, PayU, PhonePe integration" },
  { platform: "Maps & Location", tech: "Google Maps API, MapBox, Geolocation services" },
];

const BENEFITS = [
  { title: "Increased Revenue", body: "Unlock new channels: in-app purchases, subscriptions and digital services" },
  { title: "Customer Engagement", body: "Push notifications, personalised content and loyalty programs drive retention" },
  { title: "Brand Authority", body: "A polished app signals professionalism and builds long-term brand trust" },
  { title: "Operational Efficiency", body: "Automate bookings, payments, customer support and data collection" },
  { title: "Market Expansion", body: "Reach customers 24/7 regardless of geography or time zone" },
  { title: "Competitive Advantage", body: "Most SMEs still lack a good app — be the one that does" },
];

const STEPS = [
  { n: "01", title: "Discovery & Planning", body: "Requirement analysis, user persona development, feature prioritisation and project scoping." },
  { n: "02", title: "UI/UX Design", body: "Wireframes, interactive prototypes, design system and usability testing before a single line of code." },
  { n: "03", title: "Development", body: "Agile 2-week sprints with continuous client demos, code reviews and real-time progress tracking." },
  { n: "04", title: "Testing & QA", body: "Device testing across 20+ devices, performance benchmarking, security audits and UAT." },
  { n: "05", title: "App Store Launch", body: "Submission, compliance review, launch management and ASO (App Store Optimisation)." },
  { n: "06", title: "Post-Launch Support", body: "Analytics monitoring, user feedback integration, feature updates and ongoing maintenance." },
];

const FAQS = [
  {
    q: "Should I build native or cross-platform apps?",
    a: "Native apps (Swift/Kotlin) offer better performance and access to platform-specific features. Cross-platform (React Native, Flutter) is faster to develop and more cost-effective. We recommend based on your specific goals, budget and timeline.",
  },
  {
    q: "How long does mobile app development take?",
    a: "Simple apps typically take 3–6 months. Medium-complexity apps take 6–12 months. Complex enterprise applications take 12+ months. We provide a detailed timeline after the discovery phase.",
  },
  {
    q: "Can you handle both design and development?",
    a: "Yes. We provide fully integrated end-to-end services including UX research, UI design, development, QA testing, App Store submission and post-launch support.",
  },
  {
    q: "Do you support both iOS and Android?",
    a: "Yes. We build for both platforms — either as native apps (Swift for iOS, Kotlin for Android) or as a single cross-platform solution using React Native or Flutter.",
  },
  {
    q: "What payment integrations do you support?",
    a: "We integrate Razorpay, Stripe, PayU, PhonePe and other major Indian and international payment gateways depending on your business requirements.",
  },
  {
    q: "Do you provide post-launch maintenance?",
    a: "Yes. We offer flexible post-launch maintenance packages covering bug fixes, OS compatibility updates, security patches, performance optimisation and new feature development.",
  },
];

function Divider({ className }: { className?: string }) {
  return <div className={cn("w-11 h-[3px] bg-[#1565C0] mb-6", className)} />;
}

function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5", className)}>{children}</div>;
}

function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;
  const delayClass = delayClasses[i % delayClasses.length];

  return (
    <div
      ref={ref}
      className={cn(
        "border-b border-[#E2E8F0] transition-all duration-500 ease-out",
        delayClass,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
      >
        <span className="text-[15px] font-semibold text-[#0F172A] leading-[1.5]">{q}</span>
        <div
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-200",
            open ? "bg-[#1565C0] text-white" : "bg-[#F8FAFC] text-[#1565C0]"
          )}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={open ? "M2 6h8" : "M6 2v8M2 6h8"}
              stroke={open ? "#FFFFFF" : "#1565C0"}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-5">
          <p className="text-[14px] leading-[1.75] text-[#64748B]">{a}</p>
        </div>
      )}
    </div>
  );
}

function TechStackRow({ row, i }: { row: { platform: string; tech: string }; i: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;
  const delayClass = delayClasses[i % delayClasses.length];
  const bgClass = i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]";

  return (
    <div
      ref={ref}
      className={cn(
        "grid grid-cols-1 sm:grid-cols-[1fr_1.8fr] gap-4 px-4 py-3 items-center border-b border-[#E2E8F0] transition-all duration-500 ease-out",
        bgClass,
        delayClass,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-x-2"
      )}
    >
      <span className="text-[13px] font-semibold text-[#0F172A]">{row.platform}</span>
      <span className="text-[13px] leading-[1.5] text-[#64748B]">{row.tech}</span>
    </div>
  );
}

function ServiceCard({ s, i }: { s: { img: string; title: string; body: string; n: string; tags: string[] }; i: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;
  const delayClass = delayClasses[i % delayClasses.length];

  return (
    <div
      ref={ref}
      className={cn(
        "group overflow-hidden rounded-[10px] bg-white border border-[#E2E8F0] shadow-sm transition-all duration-500 ease-out",
        "hover:border-[#CBD5E1] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)]",
        delayClass,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      )}
    >
      <div className="relative h-[170px] overflow-hidden">
        <Image
          src={s.img}
          alt={s.title}
          fill
          className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 rounded-sm bg-[#1565C0] px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-white">
          {s.n}
        </div>
      </div>
      <div className="flex flex-col flex-1 p-5 pb-6">
        <h3 className="text-[14px] font-semibold text-[#0F172A] leading-tight mb-2">{s.title}</h3>
        <p className="text-[13.5px] leading-[1.75] text-[#64748B] mb-4">{s.body}</p>
        <div className="mt-auto flex flex-wrap gap-2">
          {s.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-[#EFF6FF] px-2.5 py-1 text-[11px] font-semibold text-[#1565C0]">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step({ s, i, last }: { s: { n: string; title: string; body: string }; i: number; last: boolean }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;
  const delayClass = delayClasses[i % delayClasses.length];

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex gap-4 items-start pb-8 last:pb-0 transition-all duration-500 ease-out",
        delayClass,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      {!last && <span className="absolute left-5 top-9 bottom-0 w-px bg-[#E2E8F0]" />}
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1565C0] text-[11px] font-bold text-white">
        {s.n}
      </div>
      <div className="pt-1">
        <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1">{s.title}</h3>
        <p className="text-[13.5px] leading-[1.75] text-[#64748B]">{s.body}</p>
      </div>
    </div>
  );
}

export default function MobileAppPage() {
  return (
    <main className="bg-white">
      <section className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] min-h-0 lg:min-h-[450px]">
        <div className="flex flex-col justify-center bg-white py-[30px] pl-4 sm:pl-8 lg:pl-[80px] pr-[30px]">
          <div className="flex flex-wrap items-center gap-2 text-[12px] leading-6 text-[#64748B] mb-6">
            <Link href="/services" className="font-medium text-[#1565C0] hover:text-[#0D47A1]">
              Services
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <Link href="/services/it" className="font-medium text-[#1565C0] hover:text-[#0D47A1]">
              IT Solutions
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <span>Mobile App Development</span>
          </div>

          <div className="flex flex-wrap gap-2.5 mb-5">
            {[
              "iOS & Android",
              "React Native",
              "Flutter",
              "UI/UX Design",
              "App Store",
            ].map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[#EFF6FF] px-3 py-1 text-[11px] font-semibold text-[#1565C0]"
              >
                {tag}
              </span>
            ))}
          </div>

          <Eyebrow>IT Solutions — Mobile Apps</Eyebrow>
          <Divider />
          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-[#0F172A] leading-[1.1] mb-6">
            Powerful Mobile Apps That Engage, Convert and Grow
          </h1>
          <p className="text-[16px] leading-[1.85] text-[#64748B] mb-9 ">
            From concept to App Store — we design and build innovative, scalable, user-friendly mobile applications for iOS and Android that strengthen your brand and drive real business results.
          </p>

          <div className="flex flex-wrap gap-4">
            <button className="rounded-sm bg-[#1565C0] px-7 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
              START YOUR APP
            </button>
            <button className="rounded-sm border border-[#E2E8F0] bg-white px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
              GET A FREE QUOTE
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[340px] lg:min-h-[420px]">
          <Image
            src="/images/services/IT/Banner/AppDevelopment.png"
            alt="Mobile app development"
            fill
            className="object-cover"
          />
        </div>
      </section>

      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { v: "iOS + Android", l: "Both Platforms" },
          { v: "20+", l: "Devices Tested" },
          { v: "2-Week", l: "Sprint Cycles" },
          { v: "ASO", l: "Launch Support" },
        ].map((s, i) => (
          <div key={i} className={cn("py-5 text-center", i < 3 ? "xl:border-r border-white/15" : "")}> 
            <div className="text-[20px] font-black text-white leading-none">{s.v}</div>
            <div className="text-[11px] uppercase tracking-[0.08em] text-white/70 mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow>App Development Services</Eyebrow>
              <Divider />
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] max-w-[640px]">
                  Six Capabilities. One Expert Team.
                </h2>
                <p className="max-w-[360px] text-[14px] leading-[1.85] text-[#64748B]">
                  From design to deployment and beyond — complete mobile app development under one roof.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-6 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.n} s={s} i={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 lg:items-center">
          <Reveal from="left">
            <div>
              <Eyebrow>Technology Stack</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] mb-6">
                Built on Industry-Leading Technologies
              </h2>
              <p className="mb-9 text-[14px] leading-[1.85] text-[#64748B]">
                We select the right tools for your specific platform, performance requirements and budget — ensuring your app is built to last and easy to maintain.
              </p>

              <div className="overflow-hidden rounded-[10px] border border-[#E2E8F0]">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.8fr] gap-4 bg-[#1565C0] px-4 py-3 text-[11px] uppercase font-semibold tracking-[0.1em] text-white">
                  <span>Platform</span>
                  <span>Technologies</span>
                </div>
                {TECH_STACK.map((row, i) => (
                  <TechStackRow key={row.platform} row={row} i={i} />
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative">
              <div className="relative w-full h-[480px] overflow-hidden rounded-[18px] border border-[#E2E8F0]">
                <Image
                  src="https://images.unsplash.com/photo-1551650975-87deedd944c3?w=700&q=80"
                  alt="Tech stack"
                  fill
                  className="object-cover"
                />
              </div>
              <div className="hidden lg:block absolute -bottom-6 left-0 w-full max-w-[340px] rounded-[18px] bg-white border border-[#E2E8F0] p-6 shadow-[0_8px_32px_rgba(0,0,0,0.10)]">
                <div className="text-[30px] font-black text-[#1565C0] leading-none">20+</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Devices Tested</div>
                <div className="mt-4 text-[28px] font-black text-[#E65100] leading-none">3–6 Mo</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Typical Timeline</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 lg:items-center">
          <Reveal from="left">
            <div>
              <Eyebrow>Business Benefits</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] mb-6">
                Why Your Business Needs a Mobile App
              </h2>
              <p className="mb-9 text-[14px] leading-[1.85] text-[#64748B]">
                A well-built mobile app is one of the highest-ROI investments an SME or enterprise can make — opening new revenue channels, strengthening customer relationships and improving operational efficiency.
              </p>
              <div className="space-y-4">
                {BENEFITS.map((benefit, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1565C0] text-white">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1">{benefit.title}</h3>
                      <p className="text-[13.5px] leading-[1.65] text-[#64748B]">{benefit.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative w-full h-[520px] overflow-hidden rounded-[18px] border border-[#E2E8F0]">
              <Image
                src="https://images.unsplash.com/photo-1561070791-2526d30994b5?w=700&q=80"
                alt="Business benefits of mobile apps"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 lg:items-start">
          <Reveal from="left">
            <div>
              <Eyebrow>Development Process</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                6-Stage App Development Process
              </h2>
              <p className="mb-10 text-[14px] leading-[1.85] text-[#64748B]">
                A transparent, collaborative process with client demos at every sprint, real-time progress tracking and no surprises at launch.
              </p>
              <div className="relative w-full h-[260px] overflow-hidden rounded-[18px] border border-[#E2E8F0]">
                <Image
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80"
                  alt="Agile process"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="space-y-4">
              {STEPS.map((step, index) => (
                <Step key={step.n} s={step} i={index} last={index === STEPS.length - 1} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 lg:items-start">
          <Reveal from="left">
            <div>
              <Eyebrow>Frequently Asked Questions</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] mb-6">
                Common Questions About App Development
              </h2>
              <p className="mb-9 text-[14px] leading-[1.85] text-[#64748B]">
                Clear answers to the questions we hear most from businesses planning their first — or next — mobile app.
              </p>
              <div className="relative w-full h-[180px] sm:h-[240px] lg:h-[280px] overflow-hidden rounded-[18px] border border-[#E2E8F0]">
                <Image
                  src="https://images.unsplash.com/photo-1560472355-536de3962603?w=700&q=80"
                  alt="App development FAQ"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-[#E2E8F0] pt-6">
              {FAQS.map((faq, index) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} i={index} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 lg:items-center">
          <Reveal from="left">
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[380px] overflow-hidden rounded-[18px] border border-[#E2E8F0]">
              <Image
                src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&q=80"
                alt="Start your app"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              <Eyebrow>Ready to Build Your App?</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] mb-6">
                Transform Your App Idea into Reality
              </h2>
              <p className="mb-3 text-[14px] leading-[1.85] text-[#64748B]">
                Tell us about your app idea. We will define the scope, recommend the right platform and technology and provide a detailed quote — NDA signed, no commitment required.
              </p>
              <p className="mb-9 text-[14px] leading-[1.85] text-[#64748B]">
                Fixed-price or time-and-material engagement — designed to fit your budget and timeline.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="rounded-sm bg-[#1565C0] px-8 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
                  START YOUR APP
                </button>
                <button className="rounded-sm border border-[#E2E8F0] bg-white px-8 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
                  GET A FREE QUOTE
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
